/**
 * Client HTTP direct (fetch) — partagé SaaS (mode "direct"/widget) ↔ widget.
 *
 * Factorise le JUMEAU `requestDirect` (`apiClient.ts`, mode widget du SaaS) et
 * `WidgetApi.request` (`widget/src/api.ts`) : même build d'URL, mêmes headers
 * Bearer, même sérialisation conditionnelle du body, AbortController + timeout,
 * parse tolérant, unwrap d'enveloppe et extraction d'erreur.
 *
 * Contrat : `request<T>` retourne la donnée DÉJÀ unwrappée (`data` de
 * l'enveloppe `{status,data}`) et THROW une `DirectHttpError` structurée sur
 * statut HTTP >= 400 ou erreur réseau. Les call sites qui ont besoin de
 * l'enveloppe `{status,data}` la reconstruisent eux-mêmes (cf. `requestProxy`).
 *
 * AUCUNE dépendance autre que `fetch` (global) + les helpers `envelope`.
 */

import {
  parseJsonSafe,
  unwrapEnvelope,
  extractUpstreamError,
} from './envelope'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

const METHODS_WITH_BODY: ReadonlySet<HttpMethod> = new Set<HttpMethod>([
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
])

const DEFAULT_TIMEOUT_MS = 30_000

export interface DirectHttpClientConfig {
  /** Base URL absolue du backend (ex: `https://api.ferrygate.com`). */
  baseUrl: string
  /** Clé API portée en header `Authorization: Bearer …` (optionnelle). */
  apiKey?: string
  /** Timeout par requête (ms). Défaut 30s. */
  timeoutMs?: number
}

export interface DirectRequestOptions {
  method: HttpMethod
  /** Chemin (avec ou sans `/` initial) OU body déjà sérialisé via `rawBody`. */
  path: string
  /** Body objet/valeur — sérialisé en JSON si la méthode porte un body. */
  body?: unknown
  /** Body déjà stringifié (legacy `request`) — prioritaire sur `body`. */
  rawBody?: string
  /** Headers additionnels (ex: `Accept-Language`). */
  headers?: Record<string, string>
}

/**
 * Erreur structurée levée par le client direct. Porte le statut HTTP (0 pour
 * une erreur réseau), le message lisible, le code backend et le body brut
 * parsé — de quoi reconstruire n'importe quelle shape d'erreur attendue par
 * les call sites (envelope `{status,error}`, `ApiError`, `ApiferryError`).
 */
export class DirectHttpError extends Error {
  constructor(
    readonly httpStatus: number,
    message: string,
    readonly code?: string,
    /** Body parsé de la réponse upstream (objet, string ou null). */
    readonly body?: unknown,
    /** `true` si l'erreur provient du transport (fetch a throw). */
    readonly isNetwork = false,
  ) {
    super(message)
    this.name = 'DirectHttpError'
  }
}

export interface DirectHttpClient {
  request<T>(opts: DirectRequestOptions): Promise<T>
}

/** Sérialise le body upstream selon la méthode + la priorité rawBody > body. */
function buildBody(opts: DirectRequestOptions): string | undefined {
  if (!METHODS_WITH_BODY.has(opts.method)) return undefined
  if (opts.rawBody !== undefined) return opts.rawBody
  if (opts.body === undefined) return undefined
  return typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body)
}

/**
 * Crée un client HTTP direct framework-agnostic.
 *
 * Le superset des deux twins est conservé : le timeout (présent côté SaaS) est
 * appliqué côté widget aussi, et inversement la sérialisation `rawBody` (legacy
 * `request`) du SaaS est disponible côté widget.
 */
export function createDirectHttpClient(cfg: DirectHttpClientConfig): DirectHttpClient {
  const base = cfg.baseUrl.replace(/\/$/, '')
  const timeoutMs = cfg.timeoutMs ?? DEFAULT_TIMEOUT_MS

  async function request<T>(opts: DirectRequestOptions): Promise<T> {
    const path = opts.path.startsWith('/') ? opts.path : `/${opts.path}`
    const url = base + path

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(cfg.apiKey ? { Authorization: `Bearer ${cfg.apiKey}` } : {}),
      ...opts.headers,
    }

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    let response: Response
    try {
      response = await fetch(url, {
        method: opts.method,
        headers,
        body: buildBody(opts),
        credentials: 'omit',
        signal: controller.signal,
      })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Network error'
      throw new DirectHttpError(0, message || 'Network error', 'NETWORK_ERROR', err, true)
    } finally {
      clearTimeout(timer)
    }

    if (response.status === 204) return undefined as T

    const parsed = parseJsonSafe(await response.text())

    if (response.status >= 400) {
      const { message, code } = extractUpstreamError(parsed)
      throw new DirectHttpError(
        response.status,
        message ?? `Upstream ${response.status}`,
        code,
        parsed,
      )
    }

    return unwrapEnvelope<T>(parsed)
  }

  return { request }
}
